import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';
import { useSEO, generateServiceSchema } from '../../utils/seo';

export default function CinewallMetHaardPage() {
  useSEO({
    title: 'Cinewall met haard | Elektrisch of waterdamp | cinewallkopen.nl',
    description: 'Cinewall met haard op maat. Elektrische of waterdamp haard, veilig geïntegreerd. Persoonlijk advies en gratis offerte.',
    keywords: 'cinewall met haard, elektrische haard, waterdamp haard, cinewall haard',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-met-haard`,
    schema: generateServiceSchema(
      'Cinewall met haard',
      'Maatwerk cinewalls met elektrische of waterdamp haard, veilig geïntegreerd en professioneel gemonteerd'
    ),
  });

  const haardTypes = [
    {
      title: 'Elektrische haard',
      features: [
        'Optionele warmteafgifte',
        'Zeer gebruiksvriendelijk',
        'Geen onderhoud nodig',
        'Veilig en betrouwbaar',
        'Instelbaar vlambeeld',
      ],
      image: 'https://public.readdy.ai/ai/img_res/18890a3d789fcfc2692052534f925362.jpg',
    },
    {
      title: 'Waterdamp haard',
      features: [
        'Ultrarealistische vlammen',
        'Geen warmteafgifte',
        'Luxe uitstraling',
        'Veilig voor kinderen',
        '3D vlameffect',
      ],
      image: 'https://readdy.ai/api/search-image?query=luxury%20water%20vapor%20fireplace%20with%20ultra%20realistic%203D%20flames%2C%20modern%20cinewall%20design%2C%20sophisticated%20living%20room%20interior%2C%20ambient%20lighting%2C%20high-end%20finish%2C%20warm%20neutral%20tones%2C%20professional%20photography&width=600&height=700&seq=vapor-fireplace-004&orientation=portrait',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background to-background-light"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="text-accent text-sm font-semibold uppercase tracking-wider">Cinewall met Haard</span>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-primary mt-4 mb-6 leading-tight">
              Cinewall met haard:<br />warmte en sfeer
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Een cinewall met haard combineert functionaliteit met sfeer. Of u nu kiest voor een elektrische haard met warmte of een waterdamp haard met ultrarealistische vlammen: beide opties bieden een luxe uitstraling en worden veilig geïntegreerd in uw maatwerk cinewall.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Haard Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
              Elektrisch of waterdamp?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Beide haardtypes hebben hun eigen voordelen. Wij helpen u de juiste keuze te maken op basis van uw wensen en situatie.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {haardTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-background-light rounded-2xl overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="h-80">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-primary mb-6">{type.title}</h3>
                  <ul className="space-y-3">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-check-line text-accent text-xl mr-3 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
                Waarom een cinewall met haard?
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Sfeer en warmte</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Een haard creëert direct een warme, gezellige sfeer in uw woonkamer. Perfect voor koude avonden of gewoon voor de ambiance.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Luxe uitstraling</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Een cinewall met haard is een eyecatcher die uw interieur naar een hoger niveau tilt. Modern, stijlvol en tijdloos.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Veilig geïntegreerd</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Wij zorgen voor correcte plaatsing, ventilatie en materiaalkeuze. Zo geniet u zorgeloos van uw haard.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">Op maat gemaakt</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Elke cinewall wordt afgestemd op uw ruimte, tv-formaat en persoonlijke voorkeuren. Uniek en perfect passend.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20modern%20living%20room%20with%20elegant%20cinewall%20featuring%20integrated%20fireplace%2C%20warm%20cozy%20atmosphere%2C%20LED%20accent%20lighting%2C%20sophisticated%20interior%20design%2C%20neutral%20warm%20color%20palette%2C%20professional%20photography%2C%20ultra%20realistic&width=800&height=1000&seq=cinewall-fireplace-005&orientation=portrait"
                alt="Cinewall met haard in luxe woonkamer"
                className="w-full h-[700px] object-cover object-top rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
              Hoe werkt het?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Van eerste gesprek tot oplevering: wij begeleiden u door het hele proces.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Adviesgesprek', desc: 'Bespreking van uw wensen en mogelijkheden' },
              { step: '02', title: 'Ontwerp', desc: 'Persoonlijk ontwerp op maat' },
              { step: '03', title: 'Productie', desc: 'Zorgvuldige productie met kwaliteitsmaterialen' },
              { step: '04', title: 'Montage', desc: 'Professionele plaatsing en oplevering' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-serif font-bold text-accent/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-primary mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
              Wilt u een cinewall met haard laten maken?
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Vraag vrijblijvend advies of een offerte aan. Of bezoek onze showroom om haarden in werking te zien.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <CTAButton to="/cinewall-offerte" variant="secondary">
                Vraag gratis offerte aan
              </CTAButton>
              <CTAButton to="/cinewall-showroom" className="bg-white hover:bg-gray-100 text-accent">
                Plan showroomafspraak
              </CTAButton>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}